/**
 * Material library — every product the firm uses, managed once for every
 * project (0039). Adding an item to a room copies it; nothing done here ever
 * changes a project.
 */

import { esc, fmtMoney, setPageTitle } from '@/lib/dom';
import { openModal } from '@/lib/modal';
import { toastError, toastSuccess } from '@/lib/toast';
import { getShell } from '@/lib/shell-handle';
import { dollarsToCents, centsToDollarInput } from '@/lib/money';
import { emptyToNull } from '@/lib/form-values';
import { listMaterialCategories } from '@/lib/data/material-categories';
import { indexMaterialCategories, type MaterialCategoryIndex } from '@/lib/material-categories';
import {
  listLibrary, createLibraryItem, updateLibraryItem, deleteLibraryItem,
  uploadLibraryPhoto, libraryPhotoUrl,
} from '@/lib/data/material-library';
import { searchLibrary, findLikelyDuplicate } from '@/lib/material-library';
import type { MaterialCategoryRow, MaterialLibraryRow } from '@/types/database';

export async function renderMaterialLibrary(root: HTMLElement): Promise<void> {
  root.innerHTML = `<section class="page"><div class="loading">Loading the library…</div></section>`;
  setPageTitle('Material library');
  getShell()?.setContext({ kind: 'global', active: 'library' });
  try {
    const [items, categories] = await Promise.all([
      listLibrary(),
      listMaterialCategories().catch(() => [] as MaterialCategoryRow[]),
    ]);
    paint(root, items, categories);
  } catch (err) {
    root.innerHTML = `<section class="page"><div class="auth-error">${
      esc(err instanceof Error ? err.message : 'Could not load the library')}</div></section>`;
  }
}

function paint(root: HTMLElement, items: MaterialLibraryRow[], categories: MaterialCategoryRow[]): void {
  const reload = (): void => { void renderMaterialLibrary(root); };
  const cats = indexMaterialCategories(categories);
  const byId = new Map(items.map((i) => [i.id, i] as const));

  root.innerHTML = `
    <section class="page">
      <div class="page-head">
        <h1>Material library</h1>
      </div>
      <p class="muted library-intro">
        Every product your firm uses, available in every project. Adding one to a room copies it into
        that project — changing or removing it here never alters a project that already uses it.
      </p>
      <section class="panel">
        <div class="section-bar library-toolbar">
          <div class="section-bar-left">
            <input type="search" class="library-search" data-library-search placeholder="Search by name, vendor or SKU" />
            <select data-library-category>
              <option value="">All categories</option>
              ${categories.map((c) => `<option value="${esc(c.id)}">${esc(c.name)}</option>`).join('')}
            </select>
          </div>
          <button class="btn-primary" data-action="add-library-item">+ Add product</button>
        </div>
        ${items.length === 0 ? `
          <div class="empty-inline">
            No products yet. Add them here, or use “Save to library” on any material in a room.
          </div>` : `
        <div class="table-wrap"><table class="specs-table library-table">
          <thead><tr>
            <th></th><th>Product</th><th>Category</th><th>Unit</th><th class="num">Reference price</th><th></th>
          </tr></thead>
          <tbody data-library-rows></tbody>
        </table></div>
        <div class="muted small library-count" data-library-count></div>`}
      </section>
    </section>`;

  const tbody = root.querySelector<HTMLElement>('[data-library-rows]');
  const search = root.querySelector<HTMLInputElement>('[data-library-search]')!;
  const category = root.querySelector<HTMLSelectElement>('[data-library-category]')!;
  const count = root.querySelector<HTMLElement>('[data-library-count]');

  // Only the rows re-render as you type, so the search box keeps its focus.
  const drawRows = (): void => {
    if (!tbody) return;
    const shown = searchLibrary(items, search.value, category.value || null);
    tbody.innerHTML = shown.map((i) => renderRow(i, cats)).join('')
      || `<tr><td colspan="6" class="muted">Nothing matches.</td></tr>`;
    if (count) count.textContent = `${shown.length} of ${items.length} product${items.length === 1 ? '' : 's'}`;
    tbody.querySelectorAll<HTMLImageElement>('img[data-library-photo]').forEach((img) => {
      void libraryPhotoUrl(img.dataset.libraryPhoto!).then((u) => { img.src = u; }).catch(() => { img.hidden = true; });
    });
    bindRows();
  };

  const bindRows = (): void => {
    tbody?.querySelectorAll<HTMLButtonElement>('[data-edit-library]').forEach((btn) => {
      btn.addEventListener('click', () => {
        openItemModal({ item: byId.get(btn.dataset.editLibrary!)!, items, categories, onDone: reload });
      });
    });
    tbody?.querySelectorAll<HTMLButtonElement>('[data-remove-library]').forEach((btn) => {
      btn.addEventListener('click', async () => {
        const item = byId.get(btn.dataset.removeLibrary!)!;
        if (!confirm(`Remove “${item.item}” from the library?\n\nProjects that already use it keep their own copy, unchanged.`)) return;
        btn.disabled = true;
        try {
          await deleteLibraryItem(item);
          toastSuccess(`${item.item} removed from the library`);
          reload();
        } catch (err) {
          btn.disabled = false;
          toastError(err instanceof Error ? err.message : 'Could not remove it');
        }
      });
    });
    tbody?.querySelectorAll<HTMLButtonElement>('[data-photo-library]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const item = byId.get(btn.dataset.photoLibrary!)!;
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = 'image/*';
        input.addEventListener('change', async () => {
          const file = input.files?.[0];
          if (!file) return;
          try {
            const path = await uploadLibraryPhoto(file);
            await updateLibraryItem(item.id, { photo_path: path });
            toastSuccess('Photo saved');
            reload();
          } catch (err) {
            toastError(err instanceof Error ? err.message : 'Upload failed');
          }
        });
        input.click();
      });
    });
  };

  search.addEventListener('input', drawRows);
  category.addEventListener('change', drawRows);
  root.querySelectorAll('[data-action="add-library-item"]').forEach((el) => el.addEventListener('click', () => {
    openItemModal({ items, categories, onDone: reload });
  }));
  drawRows();
}

function renderRow(i: MaterialLibraryRow, cats: MaterialCategoryIndex): string {
  return `
    <tr>
      <td class="spec-thumb-cell">${i.photo_path
        ? `<img class="spec-thumb" data-library-photo="${esc(i.photo_path)}" alt="" />`
        : `<span class="spec-thumb spec-thumb-empty" aria-hidden="true"></span>`}</td>
      <td>
        <div class="spec-item">${esc(i.item)}${i.source_url
          ? ` <a href="${esc(i.source_url)}" target="_blank" rel="noopener" class="spec-source" title="Open source">↗</a>` : ''}</div>
        <div class="muted small">${esc([i.vendor, i.sku].filter(Boolean).join(' · ') || '—')}</div>
      </td>
      <td>${cats.name(i.material_category_id) ? esc(cats.name(i.material_category_id)) : '<span class="muted">—</span>'}</td>
      <td>${esc(i.unit ?? '—')}</td>
      <td class="num">${i.unit_cost_cents == null ? '<span class="muted">—</span>' : fmtMoney(i.unit_cost_cents)}</td>
      <td class="co-actions">
        <button class="btn-link" data-photo-library="${esc(i.id)}">${i.photo_path ? 'Photo' : 'Add photo'}</button>
        <button class="icon-btn" data-edit-library="${esc(i.id)}" title="Edit">✎</button>
        <button class="btn-link btn-danger-link" data-remove-library="${esc(i.id)}">Remove</button>
      </td>
    </tr>`;
}

function openItemModal(opts: {
  item?: MaterialLibraryRow;
  items: MaterialLibraryRow[];
  categories: MaterialCategoryRow[];
  onDone: () => void;
}): void {
  const it = opts.item;
  openModal({
    title: it ? `Edit ${it.item}` : 'Add a product to the library',
    submitLabel: it ? 'Save' : 'Add to library',
    fields: [
      { name: 'item', label: 'Product', required: true, defaultValue: it?.item },
      { name: 'vendor', label: 'Vendor', defaultValue: it?.vendor ?? '' },
      { name: 'sku', label: 'SKU / model', defaultValue: it?.sku ?? '' },
      { name: 'unit', label: 'Unit (each, sq ft, yard…)', defaultValue: it?.unit ?? '' },
      { name: 'price', label: 'Reference price per unit (USD)', type: 'number',
        defaultValue: centsToDollarInput(it?.unit_cost_cents ?? null) },
      { name: 'material_category_id', label: 'Category', type: 'select',
        defaultValue: it?.material_category_id ?? '',
        options: [{ value: '', label: '— None —' }, ...opts.categories.map((c) => ({ value: c.id, label: c.name }))] },
      { name: 'source_url', label: 'Link (optional)', defaultValue: it?.source_url ?? '' },
      { name: 'notes', label: 'Notes (optional)', type: 'textarea', voice: true, defaultValue: it?.notes ?? '' },
    ],
    onSubmit: async (v) => {
      const fields = {
        item: String(v.item ?? '').trim(),
        vendor: emptyToNull(v.vendor),
        sku: emptyToNull(v.sku),
        unit: emptyToNull(v.unit),
        unit_cost_cents: dollarsToCents(String(v.price ?? '')),
        material_category_id: emptyToNull(v.material_category_id),
        source_url: emptyToNull(v.source_url),
        notes: emptyToNull(v.notes),
      };
      if (!it) {
        const dup = findLikelyDuplicate(opts.items, fields);
        if (dup && !confirm(`The library already has “${dup.item}”${dup.vendor ? ` from ${dup.vendor}` : ''}. Add another anyway?`)) return;
        await createLibraryItem(fields);
        toastSuccess(`${fields.item} added to the library`);
      } else {
        await updateLibraryItem(it.id, fields);
        toastSuccess('Saved — projects already using it are unchanged');
      }
      opts.onDone();
    },
  });
}
